import { memo, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import StarIcon from "@/shared/icons/totodoicons/solid/star.svg?react"
import MoonIcon from "@/shared/icons/totodoicons/solid/moon.svg?react"
import type { DbStartAtIndex, DbObject } from "@/db/schema"
import { StartAtType } from "@/db/schema"
import { parseDbDate } from "@totodo/operations"
import type { ViewId } from "@/features/views/viewRegistry"
import { isEvening as checkIsEvening } from "@/shared/utils/date/dateUtils"
import {
  formatStartAtChip,
  isTodayOrPast,
  isExactlyToday,
  getDeadlineText,
} from "@/shared/utils/date/dateUtils"
import { DatePickerSheet } from "@/features/schedule/DatePickerSheet"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DateChipProps {
  startAtData?: DbStartAtIndex
  viewId: ViewId
  /** For logbook/trash: statusAt or deletedAt */
  relativeDate?: string
  /** Task object — when provided, chip becomes clickable and opens schedule picker */
  task?: DbObject
  className?: string
  /** Compact mode — icon-only, no text labels */
  compact?: boolean
}

// ---------------------------------------------------------------------------
// Icon wrappers — v1 parity: pikaicons solid, 20×20
// ---------------------------------------------------------------------------

/** Star icon — indicates "today" in non-today views.
 *  overdue=true → yellow-300 (muted), overdue=false → yellow-500 (bright) */
function TodayStar({ overdue = false }: { overdue?: boolean }) {
  return (
    <StarIcon
      width={20}
      height={20}
      className={overdue ? "text-yellow-300" : "text-yellow-500"}
    />
  )
}

/** Moon icon — indicates "this evening" in non-evening views */
function EveningMoon() {
  return <MoonIcon width={20} height={20} className="text-blue-400" />
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Compact date chip for task rows.
 *
 * Rendering logic matches v1 CardPreviewStartAt:
 * - Today/Evening views: hidden (caller checks viewConfig.showDateChip)
 * - Logbook/Trash: relative time text ("Today", "3d")
 * - Evening task (not in Today/Evening): moon icon
 * - startAt today/past + not someday: star icon
 * - Future date: date chip "16 feb" in monospace font
 * - No startAt: nothing
 */
export const DateChip = memo(function DateChip({
  startAtData,
  viewId,
  relativeDate,
  task,
  className,
  compact,
}: DateChipProps) {
  const { t } = useTranslation()
  const [scheduleOpen, setScheduleOpen] = useState(false)

  const content = useMemo(() => {
    // Logbook/Trash: show relative date text
    if ((viewId === "logbook" || viewId === "trash") && relativeDate) {
      return {
        type: "text" as const,
        label: getDeadlineText(relativeDate),
      }
    }

    if (!startAtData?.startAt) return null

    const isSomeday = startAtData.type === StartAtType.Someday

    // Evening task not in Today/Evening views → moon icon
    if (
      checkIsEvening(startAtData) &&
      viewId !== "today" &&
      viewId !== "evening"
    ) {
      return { type: "moon" as const }
    }

    // A stored startAt that cannot be read as a date shows no chip (never today)
    if (parseDbDate(startAtData.startAt) === undefined) return null

    // startAt is today or past → star icon (unless Someday)
    // §4 dual-overdue: yellow-300 for past (soft overdue), yellow-500 for today
    if (
      isTodayOrPast(startAtData.startAt) &&
      !isSomeday &&
      viewId !== "today" &&
      viewId !== "evening"
    ) {
      return {
        type: "star" as const,
        overdue: !isExactlyToday(startAtData.startAt),
      }
    }

    // Future date → date chip "16 feb"
    if (!isTodayOrPast(startAtData.startAt) && viewId !== "upcoming") {
      return {
        type: "date" as const,
        label: formatStartAtChip(startAtData.startAt),
      }
    }

    return null
  }, [startAtData, viewId, relativeDate])

  if (!content) return null

  const isClickable = !!task

  let chipElement: React.ReactNode

  if (content.type === "star") {
    chipElement = (
      <span
        className={cn(
          "flex shrink-0 items-center pl-0.5 pr-1",
          !isClickable && className,
        )}
      >
        <TodayStar overdue={"overdue" in content && content.overdue} />
      </span>
    )
  } else if (content.type === "moon") {
    chipElement = (
      <span
        className={cn(
          "flex shrink-0 items-center pl-0.5 pr-1",
          !isClickable && className,
        )}
      >
        <EveningMoon />
      </span>
    )
  } else if (content.type === "text") {
    chipElement = compact ? (
      <span
        className={cn(
          "flex shrink-0 items-center pl-0.5 pr-1",
          !isClickable && className,
        )}
      >
        <TodayStar overdue={false} />
      </span>
    ) : (
      <span
        className={cn(
          "shrink-0 pl-1 pr-1.5 text-[11px] text-muted-foreground/60",
          !isClickable && className,
        )}
      >
        {content.label}
      </span>
    )
  } else {
    chipElement = compact ? (
      <span
        className={cn(
          "relative flex shrink-0 items-center pl-0.5 pr-1",
          !isClickable && className,
        )}
      >
        <StarIcon width={20} height={20} className="text-foreground/40" />
        <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-foreground/10 px-0.5 text-[7px] font-bold leading-tight text-foreground/60">
          {content.label}
        </span>
      </span>
    ) : (
      <span
        className={cn(
          "flex shrink-0 items-center rounded px-1.5 py-1.5",
          "bg-foreground/10 text-foreground/40",
          "font-mono text-[11px] font-normal leading-none",
          !isClickable && className,
        )}
      >
        {content.label}
      </span>
    )
  }

  if (!isClickable) return chipElement

  return (
    <>
      <button
        type="button"
        className={cn("cursor-pointer", className)}
        onClick={(e) => {
          e.stopPropagation()
          setScheduleOpen(true)
        }}
        aria-label={t("Change schedule")}
      >
        {chipElement}
      </button>
      <DatePickerSheet
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        task={task}
        mode="schedule"
        startAtData={startAtData}
      />
    </>
  )
})
